import React, { useEffect, useRef } from "react";
import { container } from "@/utils/mobx";
import { VirtualKeyboardStore } from "./store";
import { CursorDisplay } from "@/components/toolkit/CursorDisplay";
import "./s.virtual_keyboard.css";


export const VirtualKeyboard = container((props: {
    store: VirtualKeyboardStore;
    onKey?: (key: string) => void;
    onClose?: () => void;
    visible?: boolean;
    value?: string;
    placeholder?: string;
    cursorPos?: number;
    selStart?: number;
    selEnd?: number;
    onCursorMove?: (pos: number) => void;
    onInsertText?: (text: string) => void;
    onSelectAll?: () => void;
    showEmbedded?: boolean;
    excludeRef?: React.RefObject<Element>;
    disableAutoClose?: boolean;
    stickyControl?: React.ReactNode;
    initialCollapsed?: boolean;
    enableSplitter?: boolean;
    collapsed?: boolean;
    onVisibilityChange?: (visible: boolean) => void;
    onCollapsedChange?: (collapsed: boolean) => void;
    onHeightChange?: (height: number) => void;
    password?: boolean;
    /** Lines to show in the embedded input. >1 enables multi-line textarea mode. */
    lines?: number;
}) => {
    const store = props.store;
    const rootRef = useRef<HTMLDivElement>(null);

    // Notify parent of keyboard height so the editor can resize to avoid overlap.
    useEffect(() => {
        if (!store.visible || !rootRef.current) {
            store.onHeightChange?.(0);
            return;
        }
        if (store.collapsed) {
            store.onHeightChange?.(0);
            return;
        }
        const el = rootRef.current;
        store.onHeightChange?.(el.offsetHeight);
        const obs = new ResizeObserver(() => store.onHeightChange?.(el.offsetHeight));
        obs.observe(el);
        return () => {
            obs.disconnect();
            store.onHeightChange?.(0);
        };
    }, [store.visible, store.collapsed]);

    // Reset collapsed state when keyboard is hidden.
    useEffect(() => {
        if (!store.visible) store.setCollapsed(props.initialCollapsed ?? false);
    }, [store.visible]);

    // Close (or collapse) when the user taps outside the keyboard.
    useEffect(() => {
        if (!store.visible || store.disableAutoClose) return;
        const handler = (e: PointerEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
                // Don't close when the tap is on the trigger input itself —
                // it handles cursor repositioning and keeps the keyboard open.
                if (props.excludeRef?.current?.contains(e.target as Node)) return;
                // When splitter is present, outside tap only collapses — never hides.
                if (store.enableSplitter) {
                    if (!store.collapsed) store.setCollapsed(true);
                    return;
                }
                store.onClose?.();
            }
        };
        document.addEventListener("pointerdown", handler, { capture: true });
        return () => document.removeEventListener("pointerdown", handler, { capture: true });
    }, [store.visible, store.disableAutoClose]);

    if (!store.visible) return null;

    return (
        <div
            ref={rootRef}
            className={"vkb" + (store.collapsed ? " vkb--collapsed" : "")}
            onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
        >
            {store.enableSplitter && (
                <div
                    className={"vkb__splitter" + (store.collapsed ? " vkb__splitter--collapsed" : "")}
                    onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); store.setCollapsed(!store.collapsed); }}
                >
                    {props.stickyControl && (
                        <div className="vkb__splitter_control">{props.stickyControl}</div>
                    )}
                    <span className="vkb__splitter_chevron" />
                </div>
            )}
            {store.showEmbedded && (
                <CursorDisplay
                    className={`vkb__embedded_input${(props.lines ?? 3) > 1 ? ' vkb__embedded_input--multiline' : ''}`}
                    style={(props.lines ?? 3) > 1 ? { '--vkb-lines': props.lines ?? 3 } as React.CSSProperties : undefined}
                    value={store.value}
                    cursorPos={store.cursorPos}
                    selStart={store.selStart}
                    selEnd={store.selEnd}
                    placeholder={store.placeholder}
                    onCursorMove={(pos) => store.onCursorMove(pos)}
                    onInsertText={props.onInsertText}
                    onSelectAll={props.onSelectAll}
                    password={store.password}
                    lines={props.lines ?? 3}
                />
            )}
            {store.rows.map((row, ri) => (
                <div key={ri} className="vkb__row">
                    {row.map((key, ki) => (
                        <button
                            key={ki}
                            className={store.getKeyClass(key)}
                            onPointerDown={(e) => {
                                e.preventDefault();
                                (e.currentTarget as HTMLElement).classList.add("vkb__key--pressed");
                                if (key !== "Enter") {
                                    store.handleKey(key);
                                    store.startRepeat(key);
                                }
                            }}
                            onPointerUp={(e) => {
                                (e.currentTarget as HTMLElement).classList.remove("vkb__key--pressed");
                                store.stopRepeat();
                                if (key === "Enter") {
                                    // Swallow the click that follows this pointerUp so it doesn't
                                    // fall through to elements exposed when the keyboard closes.
                                    const swallow = (ev: Event) => {
                                        ev.stopPropagation();
                                        ev.preventDefault();
                                        document.removeEventListener("click", swallow, true);
                                    };
                                    document.addEventListener("click", swallow, true);
                                    store.handleKey(key);
                                }
                            }}
                            onPointerLeave={(e) => { (e.currentTarget as HTMLElement).classList.remove("vkb__key--pressed"); store.stopRepeat(); }}
                            onPointerCancel={(e) => { (e.currentTarget as HTMLElement).classList.remove("vkb__key--pressed"); store.stopRepeat(); }}
                        >
                            {store.getLabel(key)}
                        </button>
                    ))}
                </div>
            ))}
        </div>
    );
}, VirtualKeyboardStore);
